import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiBarChart2, FiRefreshCcw, FiBriefcase } from 'react-icons/fi';

import { analyticsApi } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';

import ActivityHeatmap from './ActivityHeatmap';
import AnalyticsSankey from './AnalyticsSankey';
import EffortOutcomeMatrix from './EffortOutcomeMatrix';
import JourneyTimeline from './JourneyTimeline';
import ResumeEvolution from './ResumeEvolution';
import StreakBar from './StreakBar';
import VelocityCards from './VelocityCards';
import WeeklyDigest from './WeeklyDigest';

/**
 * Integrated analytics dashboard. Layout (desktop, lg+):
 *
 *   ┌──────────────────── Weekly Digest (hero) ─────────────────────┐
 *   ├──────────────────── Streak Bar (4 KPIs) ──────────────────────┤
 *   ├──────────── Sankey (8) ─────────┬── Velocity (4) ─────────────┤
 *   ├── Effort vs Outcome (5) ────────┴── Resume Evolution (7) ─────┤
 *   ├──────────────────── Activity Heatmap ─────────────────────────┤
 *   └──────────────────── Journey Timeline ─────────────────────────┘
 *
 * The whole grid is constrained to ~1400px and centered so it doesn't
 * stretch into a sparse, hard-to-scan layout on ultra-wide displays.
 */
const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getDashboard();
      setPayload(res.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load analytics.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(false);
  }, []);

  const data = useMemo(() => payload || {}, [payload]);

  if (loading) {
    return <LoadingSpinner message="Loading analytics…" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center max-w-xl mx-auto my-6">
        <p className="text-red-700 font-semibold mb-1">Couldn&apos;t load analytics</p>
        <p className="text-red-600 text-sm mb-4">{error}</p>
        <button
          type="button"
          onClick={() => fetchData(false)}
          className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const heatmap = data.heatmap || { data: [], dayOfWeekDistribution: [] };
  const evolution = data.evolution || {};
  const journey = data.journey || {};
  const effort = data.effort || {};

  // Empty-state when the user has literally no events
  if (!data.hasData) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center max-w-xl mx-auto my-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-50 mb-3">
          <FiBarChart2 className="h-5 w-5 text-primary-600" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">Your analytics dashboard is ready</h3>
        <p className="text-sm text-gray-500 mb-4">
          We don&apos;t have any activity to plot yet. Save jobs, polish a resume,
          run a diagnosis, or apply to a role and your insights will start
          appearing here.
        </p>
        <div className="flex items-center justify-center gap-2">
          <Link
            to="/jobs/saved"
            className="inline-flex items-center px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-md"
          >
            <FiBriefcase className="mr-2 h-4 w-4" /> Browse saved jobs
          </Link>
          <button
            type="button"
            onClick={() => fetchData(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            <FiRefreshCcw className="mr-2 h-4 w-4" /> Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header strip: title + refresh */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-base font-bold text-gray-900 tracking-tight">Analytics</h1>
          <p className="text-xs text-gray-500">Your job-hunt insights at a glance</p>
        </div>
        <button
          type="button"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="inline-flex items-center px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-md bg-white hover:bg-gray-50 disabled:opacity-60"
        >
          <FiRefreshCcw className={`mr-1.5 h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Row 1 — Hero digest */}
        <div className="col-span-12">
          <WeeklyDigest digest={data.digest} />
        </div>

        {/* Row 2 — KPI strip */}
        <div className="col-span-12">
          <StreakBar data={data.streak} />
        </div>

        {/* Row 3 — Pipeline (left, wide) + Velocity (right, narrow stacked) */}
        <div className="col-span-12 lg:col-span-8">
          <AnalyticsSankey data={data.sankey} />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <VelocityCards data={data.velocity} stacked />
        </div>

        {/* Row 4 — Effort/Outcome (compact) + Resume Evolution (more horizontal room for the line chart) */}
        <div className="col-span-12 lg:col-span-5">
          <EffortOutcomeMatrix data={effort.data} insights={effort.insights} />
        </div>
        <div className="col-span-12 lg:col-span-7">
          <ResumeEvolution
            scoresOverTime={evolution.scoresOverTime}
            resumeEvents={evolution.resumeEvents}
            currentRadar={evolution.currentRadar}
            firstRadar={evolution.firstRadar}
          />
        </div>

        {/* Row 5 — Heatmap (full) */}
        <div className="col-span-12">
          <ActivityHeatmap
            heatmapData={heatmap.data}
            dayOfWeekDistribution={heatmap.dayOfWeekDistribution}
          />
        </div>

        {/* Row 6 — Journey timeline (full) */}
        <div className="col-span-12">
          <JourneyTimeline events={journey.events} storyArcs={journey.storyArcs} />
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
